import React, { useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AccountLayout, StatusBadge } from "../User/ResetPassword/shared";

/*
 * /unsub?tok=… — désabonnement des e-mails, depuis le lien du mail.
 * POST /api/v1/customer/unsubscriptions { unsubscription_token, unsubscription_reason }.
 * Les raisons partent en anglais, comme avant : ce sont les valeurs que le
 * back-office connaît ; seul le libellé affiché est traduit.
 */
const REASONS = [
  ["I'm receiving too many emails", "option1"],
  ["The information is not interesting", "option2"],
  ["I don't want to use your service any longer", "option3"],
  ["Other", "option4"],
];

const Unsubscribe = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [token] = useState(() => new URLSearchParams(search).get("tok"));
  const [reason, setReason] = useState(null);
  const [status, setStatus] = useState(token ? "form" : "invalid");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios({
        url: `${process.env.REACT_APP_AUTH_URL}/api/v1/customer/unsubscriptions`,
        method: "POST",
        data: { unsubscription_token: token, unsubscription_reason: reason },
      });
      setStatus("done");
    } catch (err) {
      setError(t("We couldn't unsubscribe you. Try again in a moment."));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "invalid") {
    return (
      <AccountLayout t={t}>
        <StatusBadge icon="i-mail" tone="warning" />
        <h1 className="wosh-auth__title">{t("This link doesn't work")}</h1>
        <p className="wosh-auth__lede">{t("Use the unsubscribe link at the bottom of one of our emails.")}</p>
      </AccountLayout>
    );
  }

  if (status === "done") {
    return (
      <AccountLayout t={t}>
        <StatusBadge icon="i-check" tone="success" />
        <h1 className="wosh-auth__title">{t("You're unsubscribed")}</h1>
        <p className="wosh-auth__lede">
          {t("You won't receive our emails any more. Order updates will still reach you.")}
        </p>
        <a className="wosh-auth__cta wosh-acc__cta" href="https://www.wosh.be/">
          {t("Go to wosh.be")}
        </a>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout t={t}>
      <StatusBadge icon="i-mail" />
      <h1 className="wosh-auth__title">{t("unsubscribe")}</h1>
      <p className="wosh-auth__lede">
        {t("sorry to see you unsub")} {t("tell us more")}
      </p>

      {error && <div className="wosh-auth__alert">{error}</div>}

      <form onSubmit={submit}>
        <fieldset className="wosh-acc__options">
          {REASONS.map(([value, label]) => (
            <label key={value} className={`wosh-acc__option${reason === value ? " wosh-acc__option--on" : ""}`}>
              <input
                type="radio"
                name="reason"
                value={value}
                checked={reason === value}
                onChange={() => setReason(value)}
              />
              <span className="wosh-acc__radio" />
              {t(label)}
            </label>
          ))}
        </fieldset>

        <button type="submit" className="wosh-auth__cta wosh-acc__cta" disabled={submitting}>
          {submitting && <span className="wosh-auth__spinner" />}
          {t("unsubscribe")}
        </button>
      </form>
    </AccountLayout>
  );
};

export default Unsubscribe;
